// Hide native scrollbars and hint clipped content with edge fades + chevrons
// (same visual language as SidebarModeScrollArea). Supports vertical or horizontal.
// Horizontal: wheel up/down maps to left/right; edge fades are clickable to page.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { ChevronsDown, ChevronsLeft, ChevronsRight, ChevronsUp } from "lucide-react";

const SCROLL_EPS_PX = 2;

function pageStep(el: HTMLDivElement, vertical: boolean): number {
  const span = vertical ? el.clientHeight : el.clientWidth;
  return Math.max(120, Math.round(span * 0.7));
}

export function FadeScrollArea({
  children,
  axis = "vertical",
  className = "",
  contentClassName = "",
  endAction = "page",
}: {
  children: ReactNode;
  axis?: "vertical" | "horizontal";
  /** Classes on the outer relative wrapper (sizing / flex). */
  className?: string;
  /** Classes on the scrollable element (e.g. max-height, gap, padding). */
  contentClassName?: string;
  /** Down/right chevron: page step (default) or jump to the far edge. */
  endAction?: "page" | "edge";
}) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [canStart, setCanStart] = useState(false);
  const [canEnd, setCanEnd] = useState(false);
  const vertical = axis === "vertical";

  useEffect(() => {
    const el = scrollRef.current;
    const root = rootRef.current;
    if (!el) return;

    const update = () => {
      if (vertical) {
        setCanStart(el.scrollTop > SCROLL_EPS_PX);
        setCanEnd(el.scrollHeight - el.scrollTop - el.clientHeight > SCROLL_EPS_PX);
      } else {
        setCanStart(el.scrollLeft > SCROLL_EPS_PX);
        setCanEnd(el.scrollWidth - el.scrollLeft - el.clientWidth > SCROLL_EPS_PX);
      }
    };

    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    const content = el.firstElementChild;
    if (content) ro.observe(content);
    // New chat messages / list growth — keep chevron state in sync.
    const mo = new MutationObserver(update);
    mo.observe(el, { childList: true, subtree: true, characterData: true });

    const onWheel = (e: WheelEvent) => {
      if (vertical) return;
      // Prefer native horizontal trackpad deltas; map vertical wheel → left/right.
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      if (e.deltaY === 0) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };
    const wheelTarget = root ?? el;
    wheelTarget.addEventListener("wheel", onWheel, { passive: false });

    return () => {
      el.removeEventListener("scroll", update);
      wheelTarget.removeEventListener("wheel", onWheel);
      ro.disconnect();
      mo.disconnect();
    };
  }, [vertical]);

  function scrollByPage(direction: "start" | "end") {
    const el = scrollRef.current;
    if (!el) return;
    const step = pageStep(el, vertical) * (direction === "end" ? 1 : -1);
    if (vertical) el.scrollBy({ top: step, behavior: "smooth" });
    else el.scrollBy({ left: step, behavior: "smooth" });
  }

  function scrollEnd() {
    const el = scrollRef.current;
    if (!el) return;
    if (endAction === "edge") {
      if (vertical) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
      else el.scrollTo({ left: el.scrollWidth, behavior: "smooth" });
      return;
    }
    scrollByPage("end");
  }

  const hideBar =
    "[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden";
  const fadeBase = "absolute z-[1] flex cursor-pointer border-0 bg-transparent p-0";
  const lightGradV =
    "absolute inset-0 bg-gradient-to-b from-white via-white/70 to-transparent " +
    "backdrop-blur-[2px] dark:from-zinc-950 dark:via-zinc-950/70";
  const lightGradVEnd =
    "absolute inset-0 bg-gradient-to-t from-white via-white/70 to-transparent " +
    "backdrop-blur-[2px] dark:from-zinc-950 dark:via-zinc-950/70";
  const lightGradH =
    "absolute inset-0 bg-gradient-to-r from-white via-white/70 to-transparent " +
    "backdrop-blur-[2px] dark:from-zinc-950 dark:via-zinc-950/70";
  const lightGradHEnd =
    "absolute inset-0 bg-gradient-to-l from-white via-white/70 to-transparent " +
    "backdrop-blur-[2px] dark:from-zinc-950 dark:via-zinc-950/70";

  return (
    <div ref={rootRef} className={`relative min-h-0 min-w-0 ${className}`}>
      <div
        ref={scrollRef}
        className={
          (vertical
            ? `h-full min-h-0 min-w-0 overflow-y-auto overflow-x-hidden ${hideBar}`
            : `min-h-0 min-w-0 overflow-x-auto overflow-y-hidden ${hideBar}`) +
          (contentClassName ? ` ${contentClassName}` : "")
        }
      >
        {/* Single content box so ResizeObserver tracks full scroll height. */}
        <div className={vertical ? "flex min-h-0 flex-col" : "inline-flex"}>
          {children}
        </div>
      </div>
      {canStart ? (
        vertical ? (
          <button
            type="button"
            className={`${fadeBase} inset-x-0 top-0 h-[6%] min-h-7 flex-col items-center justify-start pt-0.5`}
            aria-label="Scroll up"
            onClick={() => scrollByPage("start")}
          >
            <div className={lightGradV} />
            <ChevronsUp className="relative z-10 h-4 w-4 text-gray-400 dark:text-zinc-500" strokeWidth={2} />
          </button>
        ) : (
          <button
            type="button"
            className={`${fadeBase} inset-y-0 left-0 w-1/5 flex-row items-center justify-start pl-1`}
            aria-label="Scroll left"
            onClick={() => scrollByPage("start")}
          >
            <div className={lightGradH} />
            <ChevronsLeft className="relative z-10 h-4 w-4 text-gray-400 dark:text-zinc-500" strokeWidth={2} />
          </button>
        )
      ) : null}
      {canEnd ? (
        vertical ? (
          <button
            type="button"
            className={`${fadeBase} inset-x-0 bottom-0 h-[6%] min-h-7 flex-col items-center justify-end pb-0.5`}
            aria-label={endAction === "edge" ? "Scroll to bottom" : "Scroll down"}
            onClick={scrollEnd}
          >
            <div className={lightGradVEnd} />
            <ChevronsDown className="relative z-10 h-4 w-4 text-gray-400 dark:text-zinc-500" strokeWidth={2} />
          </button>
        ) : (
          <button
            type="button"
            className={`${fadeBase} inset-y-0 right-0 w-1/5 flex-row items-center justify-end pr-1`}
            aria-label={endAction === "edge" ? "Scroll to end" : "Scroll right"}
            onClick={scrollEnd}
          >
            <div className={lightGradHEnd} />
            <ChevronsRight className="relative z-10 h-4 w-4 text-gray-400 dark:text-zinc-500" strokeWidth={2} />
          </button>
        )
      ) : null}
    </div>
  );
}
